import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Zap, Sparkles, Crown, X, ArrowRight, Mail, Loader2 } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';

export default function PricingPage() {
  const { user } = useAuth();
  const [waitlistModalOpen, setWaitlistModalOpen] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);

  return (
    <div className="pt-32 pb-20 px-6">
      <div className="container mx-auto max-w-4xl text-center mb-8">
        <h1 className="text-5xl font-bold text-white mb-4">Simple, Transparent Pricing.</h1>
        <p className="text-xl text-slate-400">
            We’re in pilot mode. <strong>Authority</strong> is available today. Starter and Legend are on the way.
        </p>
      </div>

      <div className="container mx-auto max-w-7xl relative">
        {/* Background Glows */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[600px] bg-blue-900/20 rounded-full blur-[100px] -z-10 pointer-events-none" />

        <div className="grid md:grid-cols-3 gap-8 mb-20 items-start">
            
            {/* Plan 1: Starter (Coming Soon) */}
            <div className="bg-surface/50 border border-white/5 rounded-3xl p-8 flex flex-col relative opacity-80 hover:opacity-100 transition-opacity">
                <div className="absolute top-4 right-4 bg-white/10 text-slate-300 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                    Coming Soon
                </div>
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                        <Sparkles className="w-5 h-5 text-slate-400" />
                        The Starter
                    </h3>
                    <p className="text-sm text-slate-400 mb-6 h-10">For single-show creators who want to prove their podcast can pull its weight.</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-white">From $99</span>
                        <span className="text-slate-500 text-sm">/mo · Coming soon</span>
                    </div>
                </div>

                <ul className="space-y-4 mb-10 flex-1">
                    {[
                        'Weekly show support (up to 4 eps/mo)',
                        'Lightweight searchable topic hubs',
                        'Starter brand voice profile',
                        'Core analytics (views, clicks)',
                        'Prove-it-works plan for solo creators'
                    ].map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-400 text-sm">
                            <div className="w-5 h-5 rounded-full bg-white/5 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-slate-500" />
                            </div>
                            {feature}
                        </li>
                    ))}
                </ul>

                <button 
                    onClick={() => setWaitlistModalOpen(true)}
                    className="block w-full py-3 px-4 rounded-xl border border-white/10 hover:bg-white/5 text-slate-300 hover:text-white text-center transition-colors font-medium text-sm"
                >
                    Notify me when Starter launches
                </button>
            </div>

            {/* Plan 2: Authority (Live) */}
            <div className="bg-surface border-2 border-primary rounded-3xl p-8 flex flex-col shadow-glow-blue relative transform md:-translate-y-4 z-10">
                <div className="absolute top-0 right-0 bg-primary text-white text-[10px] font-bold px-3 py-1 rounded-bl-xl uppercase tracking-wider">
                    Early Access · Most Popular
                </div>
                
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                        <Zap className="w-5 h-5 text-primary fill-current" />
                        The Authority
                    </h3>
                    <p className="text-sm text-slate-300 mb-6 h-10">For serious brands turning their podcast into a permanent, searchable knowledge engine.</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-5xl font-bold text-white">$199</span>
                        <span className="text-slate-400 mb-1">/ month</span>
                    </div>
                </div>

                <ul className="space-y-4 mb-8 flex-1">
                    {[
                        'Up to 12 new episodes per month',
                        'Pilot back-catalog ingestion (Latest + Heroes)',
                        'Rich topic hubs for key themes',
                        'Full Brand Voice Calibration',
                        'CTA-ready brand cards',
                        'Repurposed snippets (Social/Email)',
                        'Topic & CTA Analytics',
                        'Guided onboarding + 1:1 Support'
                    ].map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-white text-sm">
                            <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-primary" />
                            </div>
                            {feature}
                        </li>
                    ))}
                </ul>

                <div className="mb-6">
                    <p className="text-[10px] text-slate-500 leading-relaxed italic">
                        * If you’re publishing more than 12 episodes a month or running multiple shows, we’ll design a Legend setup together.
                    </p>
                </div>

                <Link 
                    to={user ? "/checkout" : "/signup?mode=signup&plan=pro"} 
                    state={user ? { selectedPlan: 'pro' } : undefined}
                    className="block w-full py-4 px-4 rounded-xl bg-gradient-primary text-white font-bold text-lg text-center shadow-lg hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
                >
                    Start an Authority pilot
                    <ArrowRight className="w-5 h-5" />
                </Link>
            </div>

            {/* Plan 3: Legend (Coming Soon) */}
            <div className="bg-surface/50 border border-white/5 rounded-3xl p-8 flex flex-col relative opacity-80 hover:opacity-100 transition-opacity">
                <div className="absolute top-4 right-4 bg-purple-500/20 text-purple-300 text-[10px] font-bold px-2 py-1 rounded-full uppercase tracking-wider">
                    Enterprise · Coming Soon
                </div>
                <div className="mb-8">
                    <h3 className="text-2xl font-bold text-white mb-2 flex items-center gap-2">
                        <Crown className="w-5 h-5 text-purple-400" />
                        The Legend
                    </h3>
                    <p className="text-sm text-slate-400 mb-6 h-10">For networks and brands running multiple shows and owning their niche.</p>
                    <div className="flex items-baseline gap-1">
                        <span className="text-2xl font-bold text-white">Custom Pricing</span>
                        <span className="text-slate-500 text-sm">· Coming soon</span>
                    </div>
                </div>

                <ul className="space-y-4 mb-10 flex-1">
                    {[
                        'Everything in Authority, high volume',
                        'Central topic engine across network',
                        'Scoped back-catalog rollout',
                        'White-label topic hubs on your domain',
                        'API / Data access for internal tools',
                        'Dedicated success partnership',
                        'Custom integrations'
                    ].map((feature, i) => (
                        <li key={i} className="flex items-start gap-3 text-slate-400 text-sm">
                            <div className="w-5 h-5 rounded-full bg-purple-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <Check className="w-3 h-3 text-purple-400" />
                            </div>
                            {feature}
                        </li>
                    ))}
                </ul>

                <button 
                    onClick={() => setContactModalOpen(true)}
                    className="block w-full py-3 px-4 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 text-white text-center transition-colors font-medium text-sm"
                >
                    Talk to us about Legend
                </button>
            </div>

        </div>
        
        {/* FAQ Section */}
        <div className="max-w-3xl mx-auto border-t border-white/10 pt-16">
            <h3 className="text-2xl font-bold text-white mb-8 text-center">Common Questions about the Pilot</h3>
            <div className="space-y-6">
                <FAQItem 
                    question="How many episodes are included in the Authority pilot?"
                    answer="By default, we ingest your 8-10 most recent episodes to build your initial topic graph, plus 2-3 'hero' episodes from your back catalog that you select. Going forward, we process up to 12 new episodes per month."
                />
                <FAQItem 
                    question="What happens with our back catalogue?"
                    answer="We start with a focused set to establish your topic authority quickly. We can discuss a custom roadmap for ingesting your full archive as part of a Legend plan or a separate add-on."
                />
                <FAQItem 
                    question="Can we change which episodes are ingested first?"
                    answer="Yes. During the onboarding call, we'll review your feed and you can flag specific episodes that best represent your brand's core themes to be prioritized."
                />
            </div>
        </div>

        <div className="mt-16 text-center">
            <p className="text-sm text-slate-500">
                14-day money-back guarantee on all pilot plans.
            </p>
        </div>
      </div>

      {/* Modals */}
      <Modal open={waitlistModalOpen} onClose={() => setWaitlistModalOpen(false)} title="Join the Starter Waitlist">
          <WaitlistForm onClose={() => setWaitlistModalOpen(false)} />
      </Modal>

      <Modal open={contactModalOpen} onClose={() => setContactModalOpen(false)} title="Talk to us about Legend">
          <ContactForm onClose={() => setContactModalOpen(false)} />
      </Modal>
    </div>
  );
}

function FAQItem({ question, answer }) {
    const [open, setOpen] = useState(false);
    return (
        <div className="border-b border-white/5 pb-4">
            <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left text-lg font-medium text-slate-200 hover:text-white transition-colors">
                {question}
                <span className={`text-slate-500 transition-transform ${open ? 'rotate-45' : ''}`}>+</span>
            </button>
            {open && <p className="mt-2 text-slate-400 text-sm leading-relaxed">{answer}</p>}
        </div>
    );
}

function Modal({ open, onClose, title, children }) {
    if (!open) return null;
    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
            <div className="relative bg-surface border border-white/10 rounded-2xl p-6 max-w-md w-full shadow-2xl animate-in fade-in zoom-in duration-200">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-white">{title}</h3>
                    <button onClick={onClose} className="text-slate-500 hover:text-white">
                        <X className="w-5 h-5" />
                    </button>
                </div>
                {children}
            </div>
        </div>
    );
}

function WaitlistForm({ onClose }) {
    const [email, setEmail] = useState('');
    const [podcastLink, setPodcastLink] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 1000));
        setLoading(false);
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="text-center py-8">
                <div className="w-12 h-12 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Check className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold mb-2">You're on the list!</h4>
                <p className="text-slate-400 text-sm mb-6 leading-relaxed">
                    We’ll reach out when Starter is ready – and may invite a few people into early pilots first.
                </p>
                <button onClick={onClose} className="w-full py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors">Close</button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Email Address</label>
                <input 
                    type="email" 
                    required 
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                    placeholder="you@podcast.com"
                />
            </div>
            <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Link to your podcast <span className="text-slate-600 font-normal normal-case">(optional)</span></label>
                <input 
                    type="url" 
                    value={podcastLink}
                    onChange={(e) => setPodcastLink(e.target.value)}
                    className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                    placeholder="https://..."
                />
            </div>
            
            <div className="pt-2">
                <button type="submit" disabled={loading} className="w-full py-3 bg-primary hover:bg-primary-hover text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Notify Me'}
                </button>
                <p className="text-[10px] text-slate-500 text-center mt-3">
                    We’ll only email you about the Starter launch and early-access opportunities.
                </p>
            </div>
        </form>
    );
}

function ContactForm({ onClose }) {
    const [form, setForm] = useState({ name: '', company: '', email: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [loading, setLoading] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        // Mock API call
        await new Promise(resolve => setTimeout(resolve, 1500));
        setLoading(false);
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="text-center py-8">
                <div className="w-12 h-12 bg-purple-500/20 text-purple-400 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="w-6 h-6" />
                </div>
                <h4 className="text-white font-bold mb-2">Message Sent</h4>
                <p className="text-slate-400 text-sm mb-6">Our team will be in touch shortly to discuss your Legend setup.</p>
                <button onClick={onClose} className="w-full py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg transition-colors">Close</button>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Name</label>
                    <input 
                        type="text" 
                        required 
                        value={form.name}
                        onChange={(e) => setForm({...form, name: e.target.value})}
                        className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                        placeholder="John Doe"
                    />
                </div>
                <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Company / Brand</label>
                    <input 
                        type="text" 
                        required 
                        value={form.company}
                        onChange={(e) => setForm({...form, company: e.target.value})}
                        className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                        placeholder="Acme Robotics"
                    />
                </div>
            </div>

            <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Work Email</label>
                <input 
                    type="email" 
                    required 
                    value={form.email}
                    onChange={(e) => setForm({...form, email: e.target.value})}
                    className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all"
                    placeholder="name@company.com"
                />
            </div>
            <div>
                <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Tell us about your shows</label>
                <textarea 
                    required 
                    value={form.message}
                    onChange={(e) => setForm({...form, message: e.target.value})}
                    className="w-full bg-black/20 border border-white/10 rounded-lg p-3 text-white focus:ring-1 focus:ring-primary outline-none transition-all h-24 resize-none"
                    placeholder="How many shows? Types of shows? Rough audience size per month?"
                />
            </div>
            
            <div className="pt-2">
                <button type="submit" disabled={loading} className="w-full py-3 bg-gradient-primary text-white font-bold rounded-lg transition-colors flex items-center justify-center gap-2 shadow-glow-blue">
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Request Call'}
                </button>
                <p className="text-[10px] text-slate-500 text-center mt-3">
                    We’ll review this and follow up with next steps for a potential Legend pilot.
                </p>
            </div>
        </form>
    );
}
